import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Info, ChevronDown, ChevronRight, Warehouse, Rows3, Layers, Box } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const ZoneMapping = () => {
  const navigate = useNavigate();
  const [storageType, setStorageType] = useState('FG');
  const [hierarchy, setHierarchy] = useState([]);
  const [mappings, setMappings] = useState({
    A: [],
    B: [],
    C: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedHalls, setExpandedHalls] = useState({});
  const [expandedRows, setExpandedRows] = useState({});
  const [expandedRacks, setExpandedRacks] = useState({});

  useEffect(() => {
    fetchData();
  }, [storageType]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [hierResponse, mapResponse] = await Promise.all([
        apiClient.get(`/wms/location-hierarchy?storage_type=${storageType}`),
        apiClient.get(`/wms/zone-mapping?storage_type=${storageType}`)
      ]);

      setHierarchy(hierResponse.data.hierarchy || []);
      
      // Expand all halls by default
      const expandedH = {};
      const expandedR = {};
      const expandedRk = {};
      for (const hall of hierResponse.data.hierarchy || []) {
        expandedH[hall.location_id] = true;
        for (const row of hall.rows || []) {
          expandedR[row.row_id] = true;
          for (const rack of row.racks || []) {
            expandedRk[rack.rack_id] = true;
          }
        }
      }
      setExpandedHalls(expandedH);
      setExpandedRows(expandedR);
      setExpandedRacks(expandedRk);

      // Build mappings from response - now uses level_ids
      const newMappings = { A: [], B: [], C: [] };
      for (const mapping of mapResponse.data.mappings || []) {
        if (mapping.velocity_class in newMappings) {
          newMappings[mapping.velocity_class] = mapping.level_ids || [];
        }
      }
      setMappings(newMappings);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleLevelToggle = (velocityClass, levelId) => {
    setMappings(prev => {
      const current = prev[velocityClass] || [];
      // Remove from other classes first (a level can only be in one class)
      const otherClasses = ['A', 'B', 'C'].filter(c => c !== velocityClass);
      const newMappings = { ...prev };
      
      for (const vc of otherClasses) {
        newMappings[vc] = (newMappings[vc] || []).filter(id => id !== levelId);
      }
      
      if (current.includes(levelId)) {
        // If already in this class, remove it (toggle off)
        newMappings[velocityClass] = current.filter(id => id !== levelId);
      } else {
        // Add to this class
        newMappings[velocityClass] = [...current, levelId];
      }
      
      return newMappings;
    });
  };

  const getLevelVelocityClass = (levelId) => {
    for (const vc of ['A', 'B', 'C']) {
      if (mappings[vc]?.includes(levelId)) {
        return vc;
      }
    }
    return null;
  };

  const handleSaveAll = async () => {
    try {
      setSaving(true);
      
      // Save all three classes - now sends level_ids
      await Promise.all(['A', 'B', 'C'].map(velocityClass =>
        apiClient.post('/wms/zone-mapping', {
          storage_type: storageType,
          velocity_class: velocityClass,
          level_ids: mappings[velocityClass]
        })
      ));
      
      toast.success('Zone mappings saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save mappings');
    } finally {
      setSaving(false);
    }
  };

  const getClassColor = (velocityClass) => {
    const colors = {
      A: 'bg-green-500',
      B: 'bg-yellow-500',
      C: 'bg-red-500'
    };
    return colors[velocityClass] || 'bg-gray-400';
  };

  // Count levels by class
  const countLevelsByClass = () => {
    return {
      A: mappings.A?.length || 0,
      B: mappings.B?.length || 0,
      C: mappings.C?.length || 0,
      unassigned: getTotalLevels() - (mappings.A?.length || 0) - (mappings.B?.length || 0) - (mappings.C?.length || 0)
    };
  };

  const getTotalLevels = () => {
    let count = 0;
    for (const hall of hierarchy) {
      for (const row of hall.rows || []) {
        for (const rack of row.racks || []) {
          count += rack.levels?.length || 0;
        }
      }
    }
    return count;
  };

  const toggleHall = (hallId) => setExpandedHalls(prev => ({ ...prev, [hallId]: !prev[hallId] }));
  const toggleRow = (rowId) => setExpandedRows(prev => ({ ...prev, [rowId]: !prev[rowId] }));
  const toggleRack = (rackId) => setExpandedRacks(prev => ({ ...prev, [rackId]: !prev[rackId] }));

  const levelCounts = countLevelsByClass();

  // Bulk assign all levels in a rack
  const handleBulkAssignRack = (velocityClass, rackLevels) => {
    const levelIds = rackLevels.map(l => l.level_id);
    setMappings(prev => {
      const newMappings = { ...prev };
      // Remove from other classes
      for (const vc of ['A', 'B', 'C']) {
        if (vc !== velocityClass) {
          newMappings[vc] = (newMappings[vc] || []).filter(id => !levelIds.includes(id));
        }
      }
      // Add to this class (avoiding duplicates)
      const existing = new Set(newMappings[velocityClass] || []);
      levelIds.forEach(id => existing.add(id));
      newMappings[velocityClass] = Array.from(existing);
      return newMappings;
    });
  };

  // Bulk assign all levels in a row
  const handleBulkAssignRow = (velocityClass, rowRacks) => {
    const levelIds = [];
    for (const rack of rowRacks || []) {
      for (const level of rack.levels || []) {
        levelIds.push(level.level_id);
      }
    }
    setMappings(prev => {
      const newMappings = { ...prev };
      for (const vc of ['A', 'B', 'C']) {
        if (vc !== velocityClass) {
          newMappings[vc] = (newMappings[vc] || []).filter(id => !levelIds.includes(id));
        }
      }
      const existing = new Set(newMappings[velocityClass] || []);
      levelIds.forEach(id => existing.add(id));
      newMappings[velocityClass] = Array.from(existing);
      return newMappings;
    });
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/wms')}
              className="text-[#F9FAFB] hover:bg-white/10 mr-4"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">ABC Zone Mapping</h1>
              <p className="text-sm text-[#E8DCC8]">Assign velocity classes to Levels</p>
            </div>
          </div>
          <Button
            onClick={handleSaveAll}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="save-all-mappings"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save All Mappings'}
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Info Card */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">ABC Classification at Level</p>
              <p>Assign each Level to a velocity class (A, B, or C). During put-away, the system will suggest storage locations in levels matching the item's velocity class for optimal storage efficiency.</p>
              <p className="mt-2 font-medium">Hierarchy: Hall → Row → Rack → <span className="underline">Level (Zone assigned here)</span> → Bin</p>
            </div>
          </div>
        </div>

        {/* Storage Type & Stats */}
        <div className="bg-white rounded-lg border border-[#E5DFD0] p-6 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Label className="text-[#1A1F2E] font-medium">Storage Type:</Label>
              <Select value={storageType} onValueChange={setStorageType}>
                <SelectTrigger className="w-40" data-testid="storage-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SFG">SFG Storage</SelectItem>
                  <SelectItem value="FG">FG Storage</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Quick Stats */}
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 bg-green-500 rounded text-white flex items-center justify-center font-bold">A</span>
                <span>{levelCounts.A} levels</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 bg-yellow-500 rounded text-white flex items-center justify-center font-bold">B</span>
                <span>{levelCounts.B} levels</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 bg-red-500 rounded text-white flex items-center justify-center font-bold">C</span>
                <span>{levelCounts.C} levels</span>
              </div>
              <div className="flex items-center gap-2 text-gray-500">
                <span className="w-6 h-6 bg-gray-300 rounded text-white flex items-center justify-center font-bold">-</span>
                <span>{levelCounts.unassigned} unassigned</span>
              </div>
            </div>
          </div>
        </div>

        {/* Class Legend */}
        <div className="bg-white rounded-lg border border-[#E5DFD0] p-4 mb-6">
          <h3 className="text-sm font-medium text-[#1A1F2E] mb-3">Click A, B, or C to assign a Level to that velocity class:</h3>
          <div className="flex flex-wrap gap-6 text-sm">
            <div className="flex items-center gap-2">
              <span className="w-8 h-8 bg-green-500 rounded text-white flex items-center justify-center font-bold">A</span>
              <span>Fast-moving SKUs (prime, easy access)</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-8 h-8 bg-yellow-500 rounded text-white flex items-center justify-center font-bold">B</span>
              <span>Medium-velocity SKUs (standard access)</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-8 h-8 bg-red-500 rounded text-white flex items-center justify-center font-bold">C</span>
              <span>Slow-moving SKUs (less accessible)</span>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-[#5A5F6E]">Loading...</div>
        ) : hierarchy.length === 0 ? (
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-12 text-center">
            <p className="text-[#5A5F6E]">No locations found for {storageType} storage.</p>
            <Button
              variant="link"
              onClick={() => navigate('/wms/location-setup')}
              className="text-[#2D5F4F] mt-2"
            >
              Create locations first →
            </Button>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-6">
            <h2 className="text-lg font-semibold text-[#1A1F2E] mb-4 font-display">
              {storageType} Storage - Assign Zones to Levels
            </h2>

            <div className="space-y-3">
              {hierarchy.map((hall) => (
                <div key={hall.location_id} className="border border-[#E5DFD0] rounded-lg overflow-hidden">
                  {/* Hall Header */}
                  <div 
                    className="flex items-center justify-between p-4 bg-blue-50 cursor-pointer"
                    onClick={() => toggleHall(hall.location_id)}
                  >
                    <div className="flex items-center gap-3">
                      {hall.rows?.length > 0 ? (
                        expandedHalls[hall.location_id] ? 
                          <ChevronDown className="w-5 h-5 text-blue-600" /> : 
                          <ChevronRight className="w-5 h-5 text-blue-600" />
                      ) : <div className="w-5" />}
                      <Warehouse className="w-6 h-6 text-blue-600" />
                      <div>
                        <span className="font-semibold text-[#1A1F2E] text-lg">{hall.location_name}</span>
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded ml-2">Hall</span>
                      </div>
                      <span className="text-sm text-[#5A5F6E] ml-2">
                        ({hall.rows?.length || 0} rows)
                      </span>
                    </div>
                  </div>

                  {/* Rows */}
                  {expandedHalls[hall.location_id] && hall.rows?.length > 0 && (
                    <div className="border-t border-[#E5DFD0]">
                      {hall.rows.map((row) => (
                        <div key={row.row_id} className="border-b border-[#E5DFD0] last:border-b-0">
                          {/* Row Header */}
                          <div 
                            className="flex items-center justify-between p-3 bg-green-50 cursor-pointer"
                            onClick={() => toggleRow(row.row_id)}
                          >
                            <div className="flex items-center gap-3 ml-6">
                              {row.racks?.length > 0 ? (
                                expandedRows[row.row_id] ? 
                                  <ChevronDown className="w-4 h-4 text-green-600" /> : 
                                  <ChevronRight className="w-4 h-4 text-green-600" />
                              ) : <div className="w-4" />}
                              <Rows3 className="w-5 h-5 text-green-600" />
                              <span className="font-medium text-[#1A1F2E]">{row.row_name}</span>
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Row</span>
                              <span className="text-sm text-[#5A5F6E]">
                                ({row.racks?.length || 0} racks)
                              </span>
                            </div>
                            {/* Quick assign all levels in row */}
                            <div className="flex items-center gap-1 mr-2">
                              <span className="text-xs text-gray-500 mr-2">All:</span>
                              {['A', 'B', 'C'].map((vc) => (
                                <button
                                  key={vc}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleBulkAssignRow(vc, row.racks);
                                  }}
                                  className={`w-6 h-6 rounded text-xs font-bold ${getClassColor(vc)} text-white hover:opacity-80`}
                                  title={`Assign all levels in this row to Class ${vc}`}
                                >
                                  {vc}
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Racks */}
                          {expandedRows[row.row_id] && row.racks?.length > 0 && (
                            <div className="bg-purple-50/30">
                              {row.racks.map((rack) => (
                                <div key={rack.rack_id} className="border-t border-purple-100">
                                  {/* Rack Header */}
                                  <div 
                                    className="flex items-center justify-between p-2 cursor-pointer"
                                    onClick={() => toggleRack(rack.rack_id)}
                                  >
                                    <div className="flex items-center gap-2 ml-12">
                                      {rack.levels?.length > 0 ? (
                                        expandedRacks[rack.rack_id] ? 
                                          <ChevronDown className="w-4 h-4 text-purple-600" /> : 
                                          <ChevronRight className="w-4 h-4 text-purple-600" />
                                      ) : <div className="w-4" />}
                                      <Box className="w-4 h-4 text-purple-600" />
                                      <span className="text-sm font-medium text-[#1A1F2E]">{rack.rack_name}</span>
                                      <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">Rack</span>
                                      <span className="text-xs text-[#5A5F6E]">
                                        ({rack.levels?.length || 0} levels)
                                      </span>
                                    </div>
                                    {/* Quick assign all levels in rack */}
                                    <div className="flex items-center gap-1 mr-2">
                                      <span className="text-xs text-gray-500 mr-1">All:</span>
                                      {['A', 'B', 'C'].map((vc) => (
                                        <button
                                          key={vc}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleBulkAssignRack(vc, rack.levels);
                                          }}
                                          className={`w-5 h-5 rounded text-xs font-bold ${getClassColor(vc)} text-white hover:opacity-80`}
                                          title={`Assign all levels in this rack to Class ${vc}`}
                                        >
                                          {vc}
                                        </button>
                                      ))}
                                    </div>
                                  </div>

                                  {/* Levels */}
                                  {expandedRacks[rack.rack_id] && rack.levels?.length > 0 && (
                                    <div className="bg-orange-50/50 py-2">
                                      {rack.levels.map((level) => {
                                        const levelVelocity = getLevelVelocityClass(level.level_id);
                                        
                                        return (
                                          <div 
                                            key={level.level_id} 
                                            className="flex items-center justify-between px-4 py-1.5 ml-16"
                                          >
                                            <div className="flex items-center gap-2">
                                              <Layers className="w-4 h-4 text-orange-600" />
                                              <span className="text-sm font-medium text-[#1A1F2E]">{level.level_name}</span>
                                              <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">Level</span>
                                              <span className="text-xs text-gray-500 font-mono">{level.bin_code}</span>
                                              {levelVelocity && (
                                                <span className={`px-2 py-0.5 rounded text-xs font-bold text-white ${getClassColor(levelVelocity)}`}>
                                                  Class {levelVelocity}
                                                </span>
                                              )}
                                            </div>
                                            
                                            {/* Class Assignment Buttons */}
                                            <div className="flex items-center gap-1">
                                              {['A', 'B', 'C'].map((vc) => (
                                                <button
                                                  key={vc}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleLevelToggle(vc, level.level_id);
                                                  }}
                                                  className={`w-8 h-8 rounded font-bold text-sm transition-all ${
                                                    levelVelocity === vc 
                                                      ? `${getClassColor(vc)} text-white ring-2 ring-offset-1 scale-110` 
                                                      : 'bg-gray-200 text-gray-500 hover:bg-gray-300 hover:scale-105'
                                                  }`}
                                                  data-testid={`level-${level.level_id}-class-${vc}`}
                                                >
                                                  {vc}
                                                </button>
                                              ))}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {expandedHalls[hall.location_id] && (!hall.rows || hall.rows.length === 0) && (
                    <div className="p-4 text-center text-[#5A5F6E] text-sm border-t border-[#E5DFD0]">
                      No rows in this hall. <a href="/wms/location-setup" className="text-[#2D5F4F] underline">Add rows</a>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Summary */}
        {!loading && hierarchy.length > 0 && (
          <div className="mt-6 bg-white rounded-lg border border-[#E5DFD0] p-6">
            <h3 className="text-lg font-semibold text-[#1A1F2E] mb-4">Mapping Summary</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {['A', 'B', 'C'].map((vc) => {
                const mappedLevelIds = mappings[vc] || [];
                const mappedLevels = [];
                
                for (const hall of hierarchy) {
                  for (const row of hall.rows || []) {
                    for (const rack of row.racks || []) {
                      for (const level of rack.levels || []) {
                        if (mappedLevelIds.includes(level.level_id)) {
                          mappedLevels.push({ 
                            ...level, 
                            hallName: hall.location_name,
                            rowName: row.row_name,
                            rackName: rack.rack_name 
                          });
                        }
                      }
                    }
                  }
                }
                
                return (
                  <div key={vc} className={`p-4 rounded-lg border-2 ${
                    vc === 'A' ? 'border-green-200 bg-green-50' :
                    vc === 'B' ? 'border-yellow-200 bg-yellow-50' :
                    'border-red-200 bg-red-50'
                  }`}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className={`w-8 h-8 rounded flex items-center justify-center text-white text-lg font-bold ${getClassColor(vc)}`}>
                        {vc}
                      </span>
                      <span className="font-semibold text-lg">Class {vc}</span>
                      <span className="text-sm text-[#5A5F6E]">({mappedLevels.length} levels)</span>
                    </div>
                    {mappedLevels.length > 0 ? (
                      <ul className="text-sm text-[#5A5F6E] space-y-1 max-h-40 overflow-y-auto">
                        {mappedLevels.map(level => (
                          <li key={level.level_id} className="flex items-center gap-1">
                            <span className="text-[#1A1F2E] font-medium font-mono">{level.bin_code}</span>
                            <span className="text-xs">({level.level_name})</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-[#5A5F6E] italic">No levels assigned</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ZoneMapping;
